import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import type Keycloak from "keycloak-js";
import type { KeycloakInitOptions } from "keycloak-js";
import { useLatestRef } from "@hooks/useLatestRef";
import { setKeycloakInitialized } from "./keycloakInitState";
import { logKeycloakToken, tokenDebugInfo } from "./authDebugLog";
import { KeycloakContext, type KeycloakEventType } from "./keycloakContext";

export type {
  KeycloakContextValue,
  KeycloakEventType
} from "./keycloakContext";

interface KeycloakProviderProps {
  authClient: Keycloak;
  initOptions?: KeycloakInitOptions;
  onEvent?: (event: KeycloakEventType, error?: unknown) => void;
  onTokens?: (tokens: { token?: string }) => void;
  children: ReactNode;
}

/**
 * `keycloak.init()`을 딱 한 번만 실행하고 keycloak-js 콜백을 onEvent/onTokens props 로 매핑.
 *
 * - 콜백은 ref 로 보관해 init effect 가 안정적으로 1회만 실행되게 한다.
 * - keycloak-js 는 재초기화 시 throw 하므로 didInitRef 로 이중 init 을 방지한다.
 */
export function KeycloakProvider({
  authClient,
  initOptions,
  onEvent,
  onTokens,
  children
}: KeycloakProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const didInitRef = useRef(false);

  const onEventRef = useLatestRef(onEvent);
  const onTokensRef = useLatestRef(onTokens);
  const authClientRef = useRef(authClient);

  useEffect(() => {
    const client = authClientRef.current;

    const logClientState = (step: string) => {
      logKeycloakToken(`Provider.${step}`, {
        authenticated: client.authenticated,
        ...tokenDebugInfo(client.token),
        hasRefreshToken: Boolean(client.refreshToken)
      });
    };

    client.onReady = () => {
      logClientState("onReady(callback)");
      onEventRef.current?.("onReady");
    };
    client.onAuthSuccess = () => {
      logClientState("onAuthSuccess(callback)");
      onEventRef.current?.("onAuthSuccess");
      onTokensRef.current?.({ token: client.token });
    };
    client.onAuthError = (err) => {
      logKeycloakToken("onAuthError(callback)", {
        error: err instanceof Error ? err.message : String(err)
      });
      onEventRef.current?.("onAuthError", err);
    };
    client.onAuthRefreshSuccess = () => {
      logClientState("onAuthRefreshSuccess(callback)");
      onEventRef.current?.("onAuthRefreshSuccess");
      onTokensRef.current?.({ token: client.token });
    };
    client.onAuthRefreshError = () => {
      logKeycloakToken("onAuthRefreshError(callback)");
      onEventRef.current?.("onAuthRefreshError");
    };
    client.onAuthLogout = () => {
      logKeycloakToken("onAuthLogout(callback)");
      onEventRef.current?.("onAuthLogout");
    };
    client.onTokenExpired = () => {
      logKeycloakToken("onTokenExpired(callback)");
      onEventRef.current?.("onTokenExpired");
    };

    if (!didInitRef.current) {
      didInitRef.current = true;
      logKeycloakToken("init.start", { options: initOptions });
      client
        .init(initOptions ?? {})
        .then((authenticated) => {
          logKeycloakToken("init.resolved", {
            authenticated,
            ...tokenDebugInfo(client.token)
          });
        })
        .catch((err: unknown) => {
          logKeycloakToken("init.rejected", {
            error: err instanceof Error ? err.message : String(err)
          });
          onEventRef.current?.("onAuthError", err);
        })
        .finally(() => {
          logClientState("init.finally");
          setKeycloakInitialized(true);
          setInitialized(true);
        });
    }
  }, [initOptions, onEventRef, onTokensRef]);

  const contextValue = useMemo(
    () => ({ keycloak: authClient, initialized }),
    [authClient, initialized]
  );

  return (
    <KeycloakContext.Provider value={contextValue}>
      {children}
    </KeycloakContext.Provider>
  );
}
